import { useMemo } from 'react';
import { useLocaleState } from 'react-admin';
import type { StatusRole } from '@/lightTheme';
import type { StatEntry } from '@/features/tech-support/types';

/** The three periods the statistics endpoint counts for every account. */
export type StatPeriod = 'day' | 'week' | 'month';

/** Successful and failed requests of one account in one period. */
export const periodCounts = (entry: StatEntry, period: StatPeriod): { ok: number; err: number } => {
  if (period === 'day') return { ok: entry.lastDaySuccessfulAccess || 0, err: entry.lastDayWrongAccess || 0 };
  if (period === 'week') return { ok: entry.lastWeekSuccessfulAccess || 0, err: entry.lastWeekWrongAccess || 0 };
  return { ok: entry.lastMonthSuccessfulAccess || 0, err: entry.lastMonthWrongAccess || 0 };
};

/** Failed requests as a percentage of all requests; 0 when there were none. */
export const errorRate = (ok: number, err: number): number => {
  const total = ok + err;
  return total > 0 ? (err / total) * 100 : 0;
};

/** The health thresholds the dashboard, the system overview and the statistics share:
 *  up to 5 % of errors is healthy, up to 15 % needs attention, above that is a problem. */
export const healthTone = (rate: number): StatusRole => {
  if (rate <= 5) return 'ok';
  if (rate <= 15) return 'attention';
  return 'problem';
};

/** i18n key of the health label for an error rate. */
export const healthLabelKey = (rate: number): string => {
  if (rate <= 5) return 'dashboard.health.healthy';
  if (rate <= 15) return 'dashboard.health.warning';
  return 'dashboard.health.critical';
};

/** Totals of every account for one period. */
export const sumPeriod = (entries: StatEntry[], period: StatPeriod): { ok: number; err: number } =>
  entries.reduce(
    (acc, entry) => {
      const { ok, err } = periodCounts(entry, period);
      return { ok: acc.ok + ok, err: acc.err + err };
    },
    { ok: 0, err: 0 },
  );

const intlLocale = (locale: string) => (locale === 'es' ? 'es-ES' : 'en-GB');

/**
 * Number formats of the statistics screens, in the interface language: full counts for tables,
 * compact counts for tiles ("5,1 mil" / "5.1K"), and rates at one decimal ("1,8 %").
 */
export const useStatFormat = () => {
  const [locale] = useLocaleState();
  return useMemo(() => {
    const tag = intlLocale(locale);
    const full = new Intl.NumberFormat(tag);
    const compact = new Intl.NumberFormat(tag, { notation: 'compact', maximumFractionDigits: 1 });
    const oneDecimal = new Intl.NumberFormat(tag, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return {
      count: (n: number) => full.format(n),
      compact: (n: number) => compact.format(n),
      rate: (rate: number) => `${oneDecimal.format(rate)} %`,
    };
  }, [locale]);
};
